import { Alert, Modal } from "antd";
import React, { useState } from "react";
import { AiFillCheckCircle } from "react-icons/ai";
import intl from "react-intl-universal";
import { shallowEqual, useDispatch } from "react-redux";
import { Flex, Spacer } from "src/components";
import { StyledTitle } from "src/components/AuthenticationPage";
import Logo from "src/components/Navbar/Logo";
import { cleanLogin, requestLogin } from "src/redux/modules/Login";
import { setLoginPopup } from "src/redux/modules/Setting/actions";
import { cleanSignup } from "src/redux/modules/Signup/actions";
import { requestUserInfo } from "src/redux/modules/UserInfo";
import useSelector from "src/redux/store/useSelector";
import { theme } from "src/theme/theme";
import useGoogleAnalytics, { Category } from "src/utils/GoogleAnalytics";
import {
    dataType,
    signupResponseFailed,
    signupResponseSuccess,
} from "src/types/authentication.type";
import {
    allowSessionRefresh,
    authTitle,
    expired,
    pathInBlockerWhiteList,
    suppressLoggedOutSessionRefresh,
} from "src/utils";
import { AUTH_TITLE } from "src/utils/constants";
import ForgetPassword from "src/views/AuthenticationPage/ForgetPassword";
import Login from "src/views/AuthenticationPage/Login";
import ResetPassword from "src/views/AuthenticationPage/ResetPassword";
import Signup from "src/views/AuthenticationPage/Signup";
import SignupInfo from "src/views/AuthenticationPage/SignupInfo";
import Verification from "src/views/AuthenticationPage/Verification";
import styled from "styled-components";
import Successful from "./Successful";
import RegisterOrLoginContent from "./RegisterOrLogin";
import { client } from "src/services/client";
import { getCurrentDatetime } from "src/utils/time";
import { useSendEventBHPP } from "src/components/BHPP/Shared/useSendEventBHPP";
import { HomeIcon } from "src/static";
import { useHistory, useLocation } from "react-router-dom";

const defaultData: dataType = {
    email: "",
    password: "",
    code: "",
};

const authFailureMessage = (response: { path?: string; message?: string }) => {
    const path = response?.path || "";
    if (
        path.includes("/auth/verify") &&
        !path.includes("verifypassword") &&
        !path.includes("resend")
    ) {
        return intl.get("authentication.verification.codeInvalid");
    }
    if (
        typeof response?.message === "string" &&
        response.message.trim() &&
        response.message !== "Network Error!"
    ) {
        return response.message;
    }
    return intl.get("authentication.shared.requestFailed");
};

const Authentication = () => {
    const [data, setData] = React.useState<dataType>(defaultData);
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const [title, setTitle] = useState(authTitle(AUTH_TITLE.LOGIN));
    const [isBlocker, setIsBlocker] = useState(false);
    const [loginRequested, setLoginRequested] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [emailCheckLoading, setEmailCheckLoading] = useState(false);
    const loginAttemptedRef = React.useRef(false);
    const signupAttemptedRef = React.useRef(false);
    const verificationResentRef = React.useRef(false);
    const confirmedEmailRef = React.useRef<string | null>(null);
    const attemptedEmailRef = React.useRef("");
    const signInFailureCheckRef = React.useRef(0);
    const authFailure = useSelector((state) => state.login?.authFailure ?? null);
    const [lastUnblockedLocation, setLastUnblockedLocation] = React.useState<string>("/");
    const loginResponse = useSelector((state) => state.login?.data ?? [], shallowEqual);
    const signupResponse = useSelector((state) => state.signup?.data ?? [], shallowEqual);
    const userInfoResponse = useSelector((state) => state.userInfo?.data ?? [], shallowEqual);
    const settingsResponse = useSelector((state) => state.settings?.login ?? []);
    const { sendEventBHPP } = useSendEventBHPP();
    const { sendEvent } = useGoogleAnalytics();

    React.useEffect(() => {
        if (!authFailure?.error) {
            return;
        }
        suppressLoggedOutSessionRefresh();
        if (loginAttemptedRef.current) {
            loginAttemptedRef.current = false;
            setLoginRequested(false);
            if ((authFailure.path || "").includes("/auth/signin")) {
                explainSignInFailure(attemptedEmailRef.current);
            } else {
                setFormError(authFailureMessage(authFailure));
            }
        }
        if (!loginResponse?.cognitoJWT) {
            dispatch(cleanLogin());
        }
    }, [authFailure]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (loginResponse?.error) {
            return;
        }

        const token = loginResponse?.cognitoJWT?.access_token;
        if (token) {
            if (!expired(loginResponse, userInfoResponse)) {
                allowSessionRefresh();
                dispatch(
                    requestUserInfo({
                        Authorization: `Bearer ${token}`,
                        endpoint: "",
                        method: "GET",
                    }),
                );
                loginResponse.userProfile?.firstName
                    ? dispatch(setLoginPopup(false))
                    : setTitle(authTitle(AUTH_TITLE.SIGNUPINFO));

                if (loginResponse?.cognitoJWT?.refresh_token) {
                    localStorage.setItem(
                        "refresh_token",
                        loginResponse?.cognitoJWT?.refresh_token,
                    );
                }
                if (loginRequested) {
                    setLoginRequested(false);
                    sendEvent(
                        Category.SUBMIT,
                        `user logged in with success. ID: ${
                            loginResponse.userProfile?.profileId
                        }; web page URL: ${
                            window.location.pathname
                        }; timestamp: ${getCurrentDatetime()}; user-agent: ${
                            window.navigator.userAgent
                        }`,
                        "Authentification Index",
                    );
                }
                const BHPPAccessFrom = localStorage.getItem("_MN_BHPP_SOURCE");
                if (BHPPAccessFrom && !localStorage.getItem("_MN_BHPP_LOGIN")) {
                    sendEventBHPP(
                        Category.Other,
                        `User login after view BHPP.`,
                        "BHPP_Traffic",
                        loginResponse.userProfile?.profileId,
                    );
                    localStorage.setItem("_MN_BHPP_LOGIN", "true");
                }
            }
            return;
        }

        if (loginResponse?.requireVerification) {
            if (!verificationResentRef.current) {
                verificationResentRef.current = true;
                resendVerifyDispatch();
            }
            setTitle(authTitle(AUTH_TITLE.VERIFICATION));
            return;
        }

        // Forgot-password success has a profile id and no session token.
        if (loginResponse?.profileId) {
            if (title === authTitle(AUTH_TITLE.RESET)) {
                setTitle(authTitle(AUTH_TITLE.SUCCESS_RESET));
            } else if (loginResponse.loginType === "Email") {
                setTitle(authTitle(AUTH_TITLE.RESET));
            } else {
                setFormError(intl.get("authentication.forgotPassword.errorNotEmail"));
            }
            return;
        }

        expired(loginResponse, userInfoResponse);
    }, [loginResponse]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if ((signupResponse as signupResponseSuccess).email && !signupResponse.error) {
            setTitle(authTitle(AUTH_TITLE.VERIFICATION));
            sendEvent(
                Category.SUBMIT,
                `user sign up with success: ${signupResponse.email}`,
                "Authentification Index",
            );
        } else if ((signupResponse as signupResponseFailed) && signupResponse.error) {
            if (signupAttemptedRef.current) {
                signupAttemptedRef.current = false;
                const serverMessage =
                    typeof signupResponse.message === "string" ? signupResponse.message : "";
                setFormError(serverMessage || intl.get("authentication.shared.requestFailed"));
            }
        }
    }, [signupResponse]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        setData(defaultData);
        if (!settingsResponse.loginPopup) {
            setFormError(null);
            confirmedEmailRef.current = null;
            attemptedEmailRef.current = "";
            signInFailureCheckRef.current += 1;
        }
    }, [settingsResponse.loginPopup]);

    const previousTitle = React.useRef(title);
    React.useEffect(() => {
        if (previousTitle.current !== title) {
            previousTitle.current = title;
            setFormError(null);
            signInFailureCheckRef.current += 1;
        }
    }, [title]);

    React.useEffect(() => {
        setTitle(settingsResponse.loginTitle);
    }, [settingsResponse.loginTitle]);

    React.useEffect(() => {
        setIsBlocker(settingsResponse.isBlocker);
    }, [settingsResponse.isBlocker]);

    React.useEffect(() => {
        if (pathInBlockerWhiteList(pathname) && !pathname.includes("/listing")) {
            setLastUnblockedLocation(pathname);
        }
    }, [pathname]);

    const beginAuthAttempt = () => {
        loginAttemptedRef.current = true;
        setFormError(null);
    };

    const beginSignupAttempt = () => {
        signupAttemptedRef.current = true;
        setFormError(null);
    };

    const handleLoginForm = (body: { email: string; password: string }) => {
        sendEvent(Category.EDIT, `user interacts with login form`, "Authentification Index");
        const email = body.email.trim().toLowerCase();
        setData({
            ...data,
            email,
            password: body.password,
        });
        verificationResentRef.current = false;
        attemptedEmailRef.current = email;
        beginAuthAttempt();
        setLoginRequested(true);
        dispatch(requestLogin({ endpoint: "signin", body: { email, password: body.password }, method: "POST" }));
    };

    const explainSignInFailure = (email: string) => {
        const checkId = ++signInFailureCheckRef.current;
        const stillCurrent = () => signInFailureCheckRef.current === checkId;
        const showPasswordError = () => {
            if (stillCurrent()) {
                setFormError(intl.get("authentication.signin.wrongPassword"));
            }
        };

        if (email && confirmedEmailRef.current === email) {
            showPasswordError();
            return;
        }

        if (!email) {
            setFormError(intl.get("authentication.signin.failure"));
            return;
        }

        client({
            method: "GET",
            url: `/auth/${email}/exists`,
            headers: { "Content-Type": "application/json" },
        })
            .then((response) => response.data)
            .then((user) => {
                if (!stillCurrent()) {
                    return;
                }
                if (user?.exists) {
                    confirmedEmailRef.current = email;
                    showPasswordError();
                } else {
                    setFormError(intl.get("authentication.signin.unknownEmail"));
                }
            })
            .catch(() => {
                if (stillCurrent()) {
                    setFormError(intl.get("authentication.signin.failure"));
                }
            });
    };

    const verifyDispatch = () => {
        sendEvent(
            Category.EDIT,
            `user interacts with verifcation code: ${data.email}`,
            "Authentification Index",
        );

        let body: {
            email: string;
            password: string;
            code: string;
        } = {
            email: data.email,
            password: data.password,
            code: data.code,
        };

        beginAuthAttempt();
        dispatch(requestLogin({ endpoint: "verify", body: body, method: "POST" }));
    };

    const resendVerifyDispatch = () => {
        sendEvent(
            Category.SUBMIT,
            `user requests re-send of verification code, ${data.email}`,
            "Authentification Index",
        );

        beginAuthAttempt();
        dispatch(
            requestLogin({ endpoint: "verify/resend", body: { email: data.email }, method: "PUT" }),
        );
    };

    const fetchIfUserEmailExist = (email: string) => {
        const normalized = email.trim().toLowerCase();
        sendEvent(
            Category.SUBMIT,
            `submit on login/register form, ${normalized}`,
            "Authentification Index",
        );

        setFormError(null);
        setEmailCheckLoading(true);
        client({
            method: "GET",
            url: `/auth/${normalized}/exists`,
            headers: { "Content-Type": "application/json" },
        })
            .then((response) => response.data)
            .then((user) => {
                setData((prevState) => {
                    return { ...prevState, email: normalized };
                });
                if (user.exists) {
                    confirmedEmailRef.current = normalized;
                    dispatch(setLoginPopup(true, authTitle(AUTH_TITLE.LOGIN)));
                } else {
                    confirmedEmailRef.current = null;
                    dispatch(setLoginPopup(true, authTitle(AUTH_TITLE.SIGNUP)));
                }
            })
            .catch(() => {
                setFormError(intl.get("authentication.shared.requestFailed"));
            })
            .finally(() => setEmailCheckLoading(false));
    };

    const handleModalClose = () => {
        dispatch(setLoginPopup(false, title));
        authTitle(AUTH_TITLE.LOGIN);
        dispatch(cleanSignup());
        if (loginResponse?.error || authFailure) {
            dispatch(cleanLogin());
        }
        if (isBlocker) {
            history.push(lastUnblockedLocation);
        }
    };

    return (
        <Modal
            centered
            visible={settingsResponse.loginPopup}
            onCancel={() => handleModalClose()}
            footer={null}
            maskClosable={false}
            closeIcon={isBlocker ? <HomeIcon /> : null}
        >
            <Container column justify="center" align="stretch">
                <Logo />
                {title === authTitle(AUTH_TITLE.SUCCESS_RESET) ||
                title === authTitle(AUTH_TITLE.SUCCESS_SIGNUP) ? (
                    <AiFillCheckCircle
                        style={{
                            color: theme.color.success,
                            fontSize: "80px",
                            marginTop: "40px",
                        }}
                    />
                ) : null}
                <Spacer height={24} />
                <StyledTitle text={title} />
                {formError ? (
                    <Alert type="error" showIcon message={formError} style={{ marginTop: 16 }} />
                ) : null}

                {title === authTitle(AUTH_TITLE.LOGIN_OR_REGISTER) && (
                    <RegisterOrLoginContent
                        fetchIfUserEmailExist={fetchIfUserEmailExist}
                        loading={emailCheckLoading}
                    />
                )}
                {title === authTitle(AUTH_TITLE.SUCCESS_RESET) ||
                title === authTitle(AUTH_TITLE.SUCCESS_SIGNUP) ? (
                    <Successful setTitle={setTitle} title={title} />
                ) : null}
                {title === authTitle(AUTH_TITLE.LOGIN) ? (
                    <Login
                        handleLoginForm={handleLoginForm}
                        setTitle={setTitle}
                        setData={setData}
                        data={data}
                    />
                ) : null}
                {title === authTitle(AUTH_TITLE.FORGOT) ? (
                    <ForgetPassword
                        setData={setData}
                        setTitle={setTitle}
                        data={data}
                        onAuthAttempt={beginAuthAttempt}
                    />
                ) : null}
                {title === authTitle(AUTH_TITLE.RESET) ? (
                    <ResetPassword data={data} onAuthAttempt={beginAuthAttempt} />
                ) : null}
                {title === authTitle(AUTH_TITLE.SIGNUP) ? (
                    <Signup
                        setData={setData}
                        data={data}
                        setTitle={setTitle}
                        onAuthAttempt={beginSignupAttempt}
                    />
                ) : null}
                {title === authTitle(AUTH_TITLE.VERIFICATION) ? (
                    <Verification
                        verifyDispatch={verifyDispatch}
                        resendVerifyDispatch={resendVerifyDispatch}
                        setData={setData}
                    />
                ) : null}
                {title === authTitle(AUTH_TITLE.SIGNUPINFO) ? (
                    <SignupInfo setTitle={setTitle} />
                ) : null}
            </Container>
        </Modal>
    );
};

export default Authentication;

const Container = styled(Flex)`
    width: 100%;
    padding: 0 0 4px;
    min-width: 240px;
    z-index: 777;

    .ant-form-item {
        margin-bottom: 12px;
    }

    .ant-form-item:last-child {
        margin-bottom: 0;
    }

    .ant-input-lg,
    .ant-input-affix-wrapper-lg {
        height: 44px;
        border-radius: 10px;
    }

    .ant-input-affix-wrapper-lg > .ant-input {
        height: auto;
        border-radius: 0;
    }

    .ant-btn-lg {
        height: 44px;
        border-radius: 10px;
    }

    .ant-btn-link {
        height: auto;
        padding: 0 4px;
        line-height: 1.4;
    }
`;
